import React, { FC, ReactNode, useState } from "react";
import axiosInstant from "../../../../../api";
import { createProductPdpUrl } from "../../../../../api/endpoints";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { IVideo } from "../../hooks/useFetchVideos";
import { IconChartGlyph } from "../panels";
import { ScoreState, PrimaryButton, OutlineButton } from "./ScoreState";

/**
 * Action B — "Add this page & score it", on the No-page-registered state
 * (handoff §06 B, §10.8 / 8b).
 *
 * Asynchronous and a DURABLE WRITE, so it confirms first: the row it creates
 * persists on the product, is re-scored on its schedule for good, and shows up on the
 * product's own settings surface. The confirm is inline — it replaces the empty
 * block in place, so it inherits the panel's width and never has to work at
 * 193px as a dialog. The URL is shown in full and `word-break: break-all`,
 * because a truncated URL defeats the entire point of confirming.
 *
 * INTERNAL-ONLY IN V1. The endpoint would permit brand users, but a brand user
 * pressing this creates PRODUCT data from a surface that is not about products,
 * on a settings surface they may not be able to open — a write without a
 * corresponding read. Brand users see the state read-only, naming the product
 * and who can add it.
 *
 * The payload matches the settings modal's add exactly (`PdpUrlsManager`), so
 * a row added here is indistinguishable from one added there: stamped with
 * this video, no `video_file_url` (that is the optional "where the video is
 * embedded" field the modal also leaves empty by default).
 */

interface Product {
  desireid?: string | number;
  title?: string;
  /** `desires.url` — on the payload for ~93% of products; the FE type just drops it. */
  url?: string | null;
}

/** http(s) with a real host; prefixes https:// when the protocol is missing. Null when it won't parse. */
export const normalisePdpUrl = (raw: string | null | undefined): string | null => {
  const t = String(raw || "").trim();
  if (!t) return null;
  const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withProto);
    if (!u.hostname.includes(".")) return null;
    return u.toString();
  } catch {
    return null;
  }
};

interface Props {
  video: IVideo;
  brandId?: number;
  product: Product;
  compact?: boolean;
  /** Fires with the URL after a successful create, or `null` when the page turned
   *  out to exist already — either way the caller refreshes. */
  onAdded: (url: string | null) => void;
}

const glyph = <IconChartGlyph size={20} strokeWidth={1.6} />;

const Name: FC<{ children: ReactNode }> = ({ children }) => <span className="text-text-1">{children}</span>;

export const NoPageRegistered: FC<Props> = ({ video, brandId, product, compact = false, onAdded }) => {
  const { role } = useUser();
  const canAdd = isInternal(role) && !!brandId && !!product?.desireid;
  const known = normalisePdpUrl(product?.url);
  const [phase, setPhase] = useState<"idle" | "confirm" | "submitting">("idle");
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const name = product?.title || "this product";
  const url = known ?? normalisePdpUrl(draft);

  // Brand users, and anyone else without the write: read-only, pointing at who can add it.
  if (!canAdd) {
    return (
      <ScoreState
        skin="empty"
        icon={glyph}
        title="No product page registered"
        body={
          <>
            Your account manager can add the product page for <Name>{name}</Name> to start scoring it.
          </>
        }
        compact={compact}
      />
    );
  }

  if (phase === "idle") {
    return (
      <ScoreState
        skin="empty"
        icon={glyph}
        title="No product page registered"
        body={
          <>
            Add the page for <Name>{name}</Name> and it&apos;s scored within minutes, then re-scored on a regular schedule.
          </>
        }
        action={
          <PrimaryButton onClick={() => setPhase("confirm")} full={compact}>
            Add this page &amp; score it
          </PrimaryButton>
        }
        // The only path for anyone without the write, and the only way to add a second page.
        helper="Or add it under Edit settings → Product Pages."
        compact={compact}
      />
    );
  }

  const submit = async () => {
    if (!url || !product?.desireid) return;
    setPhase("submitting");
    setError(null);
    try {
      await axiosInstant.post(createProductPdpUrl(Number(product.desireid)), {
        pdp_url: url,
        video_file_url: null,
        brand_id: brandId,
        // Same association the settings modal makes: transcript changes then
        // regenerate this row's JSON-LD, and the row ranks first for this video.
        s3_file_id: video?.id || null,
      });
      onAdded(url);
    } catch (e: any) {
      const code = e?.response?.data?.error ?? e?.response?.data;
      if (code === "PdpUrlAlreadyExists") {
        // Not a failure — the page is registered; show whatever state it is in.
        onAdded(null);
        return;
      }
      setError("Couldn't add the page. Nothing was saved — try again.");
      setPhase("confirm");
    }
  };

  const submitting = phase === "submitting";

  return (
    <ScoreState
      skin="form"
      title={known ? "Add this page and score it?" : "Add the product page"}
      body={
        <div className="flex flex-col gap-3">
          {known ? (
            <div
              className="rounded-lg bg-surface-2 px-3 py-2 text-text-1 tabular-nums"
              style={{ fontSize: 12.5, wordBreak: "break-all" }}
            >
              {known}
            </div>
          ) : (
            <>
              <input
                type="url"
                inputMode="url"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="https://…"
                aria-label="Product page URL"
                className="h-9 px-3 rounded-[10px] border bg-surface text-text-1 w-full"
                style={{ fontSize: 13, borderColor: "var(--track)" }}
              />
              {/* The most likely wrong answer, pre-empted. */}
              <div className="text-text-2" style={{ fontSize: 11 }}>
                Use the product&apos;s own page. A category or search page would score badly and look like the product&apos;s fault.
              </div>
            </>
          )}
          {/* The consequences line names all three. */}
          <div className="text-text-2" style={{ fontSize: 12 }}>
            Scored now, then re-scored on a regular schedule. The page also appears on <Name>{name}</Name>&apos;s settings.
          </div>
          {error && (
            <div className="text-down" style={{ fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>
      }
      action={
        <>
          <PrimaryButton onClick={submit} disabled={!url || submitting} full={compact}>
            {submitting ? "Adding…" : "Add & score"}
          </PrimaryButton>
          <OutlineButton
            onClick={() => {
              setPhase("idle");
              setError(null);
            }}
            disabled={submitting}
            full={compact}
          >
            Cancel
          </OutlineButton>
        </>
      }
      compact={compact}
    />
  );
};

export default NoPageRegistered;
